import axios from 'axios';

const COMPLETE_REGISTRATION_ERROR_CONFIG = {
  duplicateAccount: {
    reason: 'duplicate-account',
    status: 409,
    messageHints: ['already exists', 'duplicate'],
  },
  invalidOrConsumedToken: {
    reason: 'invalid-or-consumed-token',
    status: 401,
    messageHints: ['invalid token', 'token expired', 'token used'],
  },
  registrationStateUncertain: {
    reason: 'registration-state-uncertain',
    status: '5xx',
  },
  unknown: {
    reason: 'unknown',
  },
} as const;

export type CompleteRegistrationErrorReason =
  (typeof COMPLETE_REGISTRATION_ERROR_CONFIG)[keyof typeof COMPLETE_REGISTRATION_ERROR_CONFIG]['reason'];

type CompleteRegistrationApiErrorContext = {
  status: number | null;
  message: string;
};

type CompleteRegistrationErrorRule = {
  reason: CompleteRegistrationErrorReason;
  status?: number | '5xx';
  messageHints?: ReadonlyArray<string>;
};

const toNormalizedErrorMessage = (value: unknown): string => {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
};

const toResponseDataMessage = (data: unknown): string => {
  if (typeof data === 'string') {
    return data;
  }

  if (!data || typeof data !== 'object' || !('message' in data)) {
    return '';
  }

  return typeof data.message === 'string' ? data.message : '';
};

const toApiErrorContext = (
  error: unknown,
): CompleteRegistrationApiErrorContext => {
  if (!axios.isAxiosError(error)) {
    return {
      status: null,
      message: toNormalizedErrorMessage(
        error instanceof Error ? error.message : '',
      ),
    };
  }

  const responseDataMessage = toNormalizedErrorMessage(
    toResponseDataMessage(error.response?.data),
  );
  return {
    status:
      typeof error.response?.status === 'number' ? error.response.status : null,
    message:
      responseDataMessage.length > 0
        ? responseDataMessage
        : toNormalizedErrorMessage(error.message),
  };
};

const COMPLETE_REGISTRATION_ERROR_RULES = [
  COMPLETE_REGISTRATION_ERROR_CONFIG.duplicateAccount,
  COMPLETE_REGISTRATION_ERROR_CONFIG.invalidOrConsumedToken,
  COMPLETE_REGISTRATION_ERROR_CONFIG.registrationStateUncertain,
] as const satisfies ReadonlyArray<CompleteRegistrationErrorRule>;

const includesAnyMessageHint = (
  message: string,
  hints: ReadonlyArray<string>,
): boolean => {
  return hints.some((hint) => message.includes(hint));
};

const matchesRuleStatus = (
  status: number | null,
  ruleStatus: CompleteRegistrationErrorRule['status'],
): boolean => {
  if (status === null || ruleStatus === undefined) {
    return false;
  }

  if (ruleStatus === '5xx') {
    return status >= 500;
  }

  return status === ruleStatus;
};

const matchesRule = (
  context: CompleteRegistrationApiErrorContext,
  rule: CompleteRegistrationErrorRule,
): boolean => {
  return (
    matchesRuleStatus(context.status, rule.status) ||
    (rule.messageHints
      ? includesAnyMessageHint(context.message, rule.messageHints)
      : false)
  );
};

const toCompleteRegistrationErrorReason = (
  error: unknown,
): CompleteRegistrationErrorReason => {
  const context = toApiErrorContext(error);
  const matchedRule = COMPLETE_REGISTRATION_ERROR_RULES.find((rule) =>
    matchesRule(context, rule),
  );

  return (
    matchedRule?.reason ?? COMPLETE_REGISTRATION_ERROR_CONFIG.unknown.reason
  );
};

export class CompleteRegistrationError extends Error {
  readonly reason: CompleteRegistrationErrorReason;

  constructor(reason: CompleteRegistrationErrorReason, message?: string) {
    super(message ?? 'Complete registration failed');
    this.name = 'CompleteRegistrationError';
    this.reason = reason;
  }
}

export const toCompleteRegistrationError = (
  error: unknown,
): CompleteRegistrationError => {
  if (error instanceof CompleteRegistrationError) {
    return error;
  }

  const reason = toCompleteRegistrationErrorReason(error);
  return new CompleteRegistrationError(reason);
};

export const isRecoverableCompleteRegistrationError = (
  error: unknown,
): error is CompleteRegistrationError => {
  return (
    error instanceof CompleteRegistrationError &&
    error.reason !== COMPLETE_REGISTRATION_ERROR_CONFIG.unknown.reason
  );
};
